import React, { useMemo, useState, useCallback } from 'react';
import {
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Card,
  Stack,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Delete, MoreVert, Search } from '@mui/icons-material';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import RoleSelector from '@/components/selectors/RoleSelector';
import PvdConfigPicker from '@/components/selectors/PvdConfigPicker';
import EmergencyConfigPicker from '@/components/selectors/EmergencyConfigPicker';
import GroupSelector from '@/components/selectors/GroupSelector';
import { GridColDef } from '@mui/x-data-grid';
import { OrgUser } from '@/models/User/OrgUser';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import {
  PersistentQuickFilterToolbar,
  SEARCH_FIELD_BORDER_RADIUS,
} from '@/components/data/PersistentQuickFilterToolbar';
import Group from '@/models/Organization/Group';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import UserDashboard from '../home/user_dashboard/UserDashboard';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import {
  StatusStoreState,
  useAllGroupUserStatuses,
} from '@/context_providers/group_user_context/OrgGroupUserStatusStore';
import {
  OrganizationOnAzure,
  SubscriptionTier,
} from '../../../../generated-client/models';
import { useSetUserTier } from '@/hooks/useSetUserTier';
import TierSelector from '@/components/selectors/TierSelector';
import { getUserAssignedTier } from '@/utils/organization/getUserAssignedTier';
import { getIsOrgOnTrial } from '@/utils/organization/getIsOrgOnTrial';
import { buildPoolCounts, hasAvailableSeat } from '@/utils/organization/orgSeatAvailability';
import { getTierLabelKey } from '@/utils/entitlements/tierLabels';
import { isAdmin } from '@/components/routing/UserRoutes/AcceptedRoles';
import { getGroupNameForUser } from '@/utils/organization/getGroupNameForUser';
import { getConfigNameById } from '@/utils/organization/getConfigNameById';

const DEFAULT_INITIAL_PAGE_SIZE = 15;
const DEFAULT_PAGE_SIZE_OPTIONS = [15, 25, 50];
const DEFAULT_GRID_WRAPPER_HEIGHT = 'calc(100vh - 280px)';
const LAST_ACCESSED_FALLBACK = '--';
const LAST_ACCESSED_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};
const LAST_ACCESSED_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};
const REMOVE_USER_COLUMN_MIN_WIDTH = 80;
const LAST_ACCESSED_COLUMN_MIN_WIDTH = 110;
const ROLE_COLUMN_MIN_WIDTH = 170;
const ROLE_SELECTOR_MIN_WIDTH = 135;
const COMPACT_COLUMN_MIN_WIDTH = 140;
const COMPACT_SELECTOR_MIN_WIDTH = 110;
const TABLE_FONT_SIZE = 14;

function getLastAccessedParts(
  timestamp: number | null | undefined,
): { date: string; time: string } | null {
  if (timestamp == null || timestamp <= 0) {
    return null;
  }

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return {
    date: date.toLocaleDateString(undefined, LAST_ACCESSED_DATE_FORMAT),
    time: date.toLocaleTimeString(undefined, LAST_ACCESSED_TIME_FORMAT),
  };
}

function formatLastAccessed(timestamp: number | null | undefined): string {
  const parts = getLastAccessedParts(timestamp);
  if (!parts) {
    return LAST_ACCESSED_FALLBACK;
  }

  return `${parts.date}\n${parts.time}`;
}

function LastAccessedDisplay({
  timestamp,
}: {
  timestamp: number | null | undefined;
}) {
  const parts = getLastAccessedParts(timestamp);

  if (!parts) {
    return (
      <Typography sx={{ fontSize: TABLE_FONT_SIZE }}>
        {LAST_ACCESSED_FALLBACK}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Typography sx={{ fontSize: TABLE_FONT_SIZE }}>{parts.date}</Typography>
      <Typography sx={{ color: COLORS.GREY, fontSize: TABLE_FONT_SIZE }}>
        {parts.time}
      </Typography>
    </Box>
  );
}

interface UserTableProps {
  users: OrgUser[];
  setUserToHighlight: React.Dispatch<React.SetStateAction<string | null>>;
  userToHighlight: string | null;
  organization: OrganizationOnAzure | null;
  groupName: string;
  orgUser: OrgUser | null;
  setConfirmTitle: (title: string) => void;
  setConfirmMessage: (message: string) => void;
  setSelectedUser: (user: OrgUser) => void;
  setShowConfirmModal: (show: boolean) => void;
  allGroups: Group[];
  initialPageSize?: number;
  pageSizeOptions?: number[];
  gridHeight?: string | number;
  toolbarAlignRight?: boolean;
  quickFilterPlaceholder?: string;
}

/**
 *
 * @param {UserTableProps} props
 * @returns
 */
const UserTable = ({
  users,
  userToHighlight,
  setUserToHighlight,
  groupName,
  orgUser,
  setConfirmTitle,
  setConfirmMessage,
  setSelectedUser,
  setShowConfirmModal,
  allGroups,
  initialPageSize,
  pageSizeOptions,
  gridHeight,
  toolbarAlignRight,
  quickFilterPlaceholder,
}: UserTableProps) => {
  const { organization } = useOrganizationUpdaterProvider();
  const groupStatuses = useAllGroupUserStatuses();

  if (!organization || !groupStatuses || !orgUser)
    return null;

  return (
    <UserTableContents
      users={users}
      userToHighlight={userToHighlight}
      setUserToHighlight={setUserToHighlight}
      groupName={groupName}
      orgUser={orgUser}
      setConfirmTitle={setConfirmTitle}
      setConfirmMessage={setConfirmMessage}
      setSelectedUser={setSelectedUser}
      setShowConfirmModal={setShowConfirmModal}
      allGroups={allGroups}
      initialPageSize={initialPageSize}
      pageSizeOptions={pageSizeOptions}
      gridHeight={gridHeight}
      toolbarAlignRight={toolbarAlignRight}
      quickFilterPlaceholder={quickFilterPlaceholder}
      // provider values
      organization={organization}
      groupStatuses={groupStatuses}
    />
  );
};

/**
 * Child component which contains the original implementation and may
 * safely assume `organization` is defined.
 * @param {UserTableProps & {
 *  organization: OrganizationOnAzure,
 *  groupStatuses: StatusStoreState,
 * }}
 */
const UserTableContents = ({
  users,
  userToHighlight,
  setUserToHighlight,
  groupName,
  orgUser,
  setConfirmTitle,
  setConfirmMessage,
  setSelectedUser,
  setShowConfirmModal,
  allGroups,
  initialPageSize = DEFAULT_INITIAL_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  gridHeight,
  toolbarAlignRight,
  quickFilterPlaceholder,
  organization,
  groupStatuses,
}: UserTableProps & {
  organization: OrganizationOnAzure;
  groupStatuses: StatusStoreState;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dashboardActions = useDashboardActions();
  const { t } = useTranslation();
  const effectiveQuickFilterPlaceholder =
    quickFilterPlaceholder ?? t(StringKeys.SEARCH_USERS);
  const viewerIsAdmin = isAdmin(orgUser?.role);
  // pvdConfigurations and emergencyConfigurations are added to OrganizationContextType in Task 25
  const {
    pvdConfigurations,
    emergencyConfigurations,
    updateOrganization,
  } = useOrganizationUpdaterProvider() as ReturnType<typeof useOrganizationUpdaterProvider> & {
    pvdConfigurations: Array<{ id: string; spec_name: string; is_base_config?: boolean }> | null;
    emergencyConfigurations: Array<{ id: string; spec_name: string; is_base_config?: boolean }> | null;
  };

  // Groups keyed the same way `useSetUserTier` (and getUserAssignedTier) expect —
  // an array of the raw per-group auth-id lists, not the `Group` model used for
  // GroupSelector/UserDashboard.
  const orgGroups = useMemo(
    () => Object.values(organization.groups ?? {}),
    [organization],
  );

  // Effective-tier gating for the config pickers is trial-aware (nm-3): an active org
  // trial elevates every user to Respond, so both pickers become interactive.
  const isOrgOnTrial = useMemo(
    () => getIsOrgOnTrial(organization.trial_end_timestamp),
    [organization.trial_end_timestamp],
  );

  // Tiers with no remaining seats org-wide — computed once per render rather
  // than per row since it only depends on the organization, not the user.
  const fullPools = useMemo(
    () => {
      const poolCounts = buildPoolCounts(organization);
      return [SubscriptionTier.Ops, SubscriptionTier.Respond].filter(
        (tier) => !hasAvailableSeat(poolCounts, tier),
      );
    },
    [organization],
  );

  const updateAssignedTierLocally = useCallback(
    (tier: SubscriptionTier, user: { auth_id: string }, changedGroupName: string) => {
      const groups = organization.groups ?? {};
      const currentGroup = groups[changedGroupName];
      if (!currentGroup) return;

      const withoutUser = (ids?: string[]) =>
        (ids ?? []).filter((id) => id !== user.auth_id);

      const nextGroup = {
        ...currentGroup,
        connect_user_auth_ids: withoutUser(currentGroup.connect_user_auth_ids),
        ops_user_auth_ids: withoutUser(currentGroup.ops_user_auth_ids),
        respond_user_auth_ids: withoutUser(currentGroup.respond_user_auth_ids),
      };

      if (tier === SubscriptionTier.Ops) {
        nextGroup.ops_user_auth_ids = [
          ...nextGroup.ops_user_auth_ids,
          user.auth_id,
        ];
      } else if (tier === SubscriptionTier.Respond) {
        nextGroup.respond_user_auth_ids = [
          ...nextGroup.respond_user_auth_ids,
          user.auth_id,
        ];
      } else {
        nextGroup.connect_user_auth_ids = [
          ...nextGroup.connect_user_auth_ids,
          user.auth_id,
        ];
      }

      updateOrganization({
        ...organization,
        groups: {
          ...groups,
          [changedGroupName]: nextGroup,
        },
      });
    },
    [organization, updateOrganization],
  );

  const { changeUserTier, inProgress } = useSetUserTier({
    updateLocalState: updateAssignedTierLocally,
    highlightUser: setUserToHighlight,
  });

  const resolveGroupName = useCallback(
    (user: OrgUser) => getGroupNameForUser(allGroups, user.auth_id) || groupName,
    [allGroups, groupName],
  );

  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [selectedUser, setSelectedUserLocal] = useState<OrgUser | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  // Debounce search input to reduce re-renders while typing on mobile
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    user: OrgUser,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUserLocal(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserLocal(null);
  };

  const handleDeleteUserFromMenu = () => {
    if (selectedUser) {
      handleDeleteUser(selectedUser);
    }
    handleMenuClose();
  };

  const handleDeleteUser = useCallback(
    (user: OrgUser) => {
      setConfirmTitle(
        t(StringKeys.REMOVE_USER_X, {
          user: `${user.first_name} ${user.last_name}`,
          group: resolveGroupName(user),
        }),
      );
      setConfirmMessage(
        t(
          StringKeys.ARE_YOU_SURE_YOU_WOULD_LIKE_TO_REMOVE_USER_FROM_ORGANIZATION,
          {
            user: `${user.first_name} ${user.last_name}`,
            organization: organization.company_name,
          },
        ),
      );
      setSelectedUser(user);
      setShowConfirmModal(true);
    },
    [
      t,
      resolveGroupName,
      organization,
      setConfirmTitle,
      setConfirmMessage,
      setSelectedUser,
      setShowConfirmModal,
    ],
  );

  // columns can be memoized if you want

  const columns = useMemo(() => {
    const baseColumns: GridColDef<OrgUser>[] = [
      {
        field: 'userName',
        type: 'string',

        headerName: t(StringKeys.USER_NAME),
        headerClassName: 'first',
        minWidth: 175,
        flex: 0.6,
        display: 'flex',
        align: 'left',
        headerAlign: 'left',

        valueGetter: (_, row) => `${row.first_name} ${row.last_name}`.trim(),
        renderCell: ({ row, value }) => (
          <Box
            onClick={(e) => {
              e.stopPropagation(); // Prevents row click if you have row click handlers
              dashboardActions.openDashboard(row.auth_id);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
              pl: 3,
              justifyContent: 'center',
              '&:hover .user-name': {
                textDecoration: 'underline',
              },
            }}
          >
            <Typography
              className="user-name"
              sx={{ fontWeight: 400, fontSize: TABLE_FONT_SIZE }}
            >
              {value}
            </Typography>
            <Typography sx={{ color: COLORS.GREY, fontSize: 14 }}>
              {row.phone_number}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'role',
        headerName: t(StringKeys.ROLE),
        type: 'custom',
        align: 'left',
        headerAlign: 'left',
        minWidth: ROLE_COLUMN_MIN_WIDTH,
        flex: 0.45,
        display: 'flex',
        renderCell: ({ row }) => (
          <RoleSelector
            orgAzureId={organization.id}
            orgCode={organization.org_code}
            roleList={Object.keys(organization.roles_with_features_default)}
            user={row}
            userToHighlight={setUserToHighlight}
            sx={{ minWidth: ROLE_SELECTOR_MIN_WIDTH }}
            fontSize={TABLE_FONT_SIZE}
          />
        ),
      },
      {
        field: 'pvdConfig',
        headerName: t(StringKeys.COMPASS_CONFIGURATION),
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        minWidth: 175,
        flex: 0.5,
        display: 'flex',
        valueGetter: (_, row) =>
          getConfigNameById(pvdConfigurations, row.pvd_config_id),
        renderCell: ({ row }) => (
          <PvdConfigPicker
            orgCode={organization.org_code}
            configList={(pvdConfigurations ?? []).map((c) => ({
              id: c.id,
              name: c.spec_name,
              is_base_config: c.is_base_config,
            }))}
            user={row}
            userToHighlight={setUserToHighlight}
            groups={orgGroups}
            isOrgOnTrial={isOrgOnTrial}
            fontSize={TABLE_FONT_SIZE}
          />
        ),
      },
      {
        field: 'emergencyConfig',
        headerName: t(StringKeys.EMERGENCY_CONFIGURATION),
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        minWidth: 175,
        flex: 0.5,
        display: 'flex',
        valueGetter: (_, row) =>
          getConfigNameById(emergencyConfigurations, row.emergency_config_id),
        renderCell: ({ row }) => (
          <EmergencyConfigPicker
            orgCode={organization.org_code}
            configList={(emergencyConfigurations ?? []).map((c) => ({
              id: c.id,
              name: c.spec_name,
              is_base_config: c.is_base_config,
            }))}
            user={row}
            userToHighlight={setUserToHighlight}
            groups={orgGroups}
            isOrgOnTrial={isOrgOnTrial}
            fontSize={TABLE_FONT_SIZE}
          />
        ),
      },
      {
        field: 'subscribed',
        type: 'custom',
        headerName: t(StringKeys.SUBSCRIPTION),
        flex: 0.3,
        minWidth: COMPACT_COLUMN_MIN_WIDTH,
        align: 'left',
        headerAlign: 'left',
        display: 'flex',
        valueGetter: (_, row) => getUserAssignedTier(orgGroups, row.auth_id),

        renderCell: ({ row }) => {
          const assignedTier = getUserAssignedTier(orgGroups, row.auth_id);
          if (!viewerIsAdmin) {
            return (
              <Typography sx={{ fontSize: TABLE_FONT_SIZE }}>
                {getTierLabelKey(assignedTier)}
              </Typography>
            );
          }
          return (
            <TierSelector
              value={assignedTier}
              fullPools={fullPools}
              disabled={!viewerIsAdmin || inProgress}
              showTrialElevationInfo={isOrgOnTrial}
              onChange={(tier) => changeUserTier(tier, row, resolveGroupName(row))}
              sx={{ minWidth: COMPACT_SELECTOR_MIN_WIDTH }}
              fontSize={TABLE_FONT_SIZE}
            />
          );
        },
      },
      {
        field: 'changeGroup',
        headerName: t(StringKeys.CHANGE_GROUP),
        align: 'left',
        headerAlign: 'left',
        minWidth: 175,
        display: 'flex',
        flex: 0.5,
        filterable: false,
        type: 'string',
        valueGetter: (_, row) => resolveGroupName(row),
        renderCell: ({ row }) => (
          <GroupSelector
            allGroups={allGroups}
            currentGroup={resolveGroupName(row)}
            user={row}
            orgAzureId={organization.id}
            orgCode={organization.org_code}
            userToHighlight={setUserToHighlight}
            fontSize={TABLE_FONT_SIZE}
          />
        ),
      },
      {
        field: 'lastAccessed',
        headerName: t(StringKeys.LAST_ACCESSED),
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        minWidth: LAST_ACCESSED_COLUMN_MIN_WIDTH,
        flex: 0.3,
        display: 'flex',
        valueGetter: (_, row) => row.last_interaction_timestamp ?? null,
        valueFormatter: (value: number | null) => formatLastAccessed(value),
        renderCell: ({ value }) => (
          <LastAccessedDisplay timestamp={value as number | null} />
        ),
      },
      {
        field: 'removeUser',
        headerName: t(StringKeys.REMOVE_USER),
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        filterable: false,
        display: 'flex',
        minWidth: REMOVE_USER_COLUMN_MIN_WIDTH,
        flex: 0.25,
        renderCell: ({ row }) =>
          row.auth_id !== orgUser?.auth_id ? (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteUser(row);
              }}
              sx={{
                backgroundColor: COLORS.PINK,
                borderRadius: '10px',
                width: 40,
                height: 40,
                '&:hover': { backgroundColor: COLORS.HOVER_RED },
              }}
            >
              <Delete sx={{ color: COLORS.DARK_RED }} />
            </IconButton>
          ) : null,
      },
    ];
    return baseColumns;
  }, [
    organization,
    pvdConfigurations,
    emergencyConfigurations,
    orgGroups,
    isOrgOnTrial,
    fullPools,
    viewerIsAdmin,
    inProgress,
    changeUserTier,
    resolveGroupName,
    orgUser,
    setUserToHighlight,
    allGroups,
    t,
    handleDeleteUser,
    dashboardActions,
  ]);

  return (
    <>
      {!isMobile ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height:
              gridHeight === '100%'
                ? '100%'
                : gridHeight ?? DEFAULT_GRID_WRAPPER_HEIGHT,
            width: '100%',
            minHeight: gridHeight === '100%' ? 0 : undefined,
            maxHeight: gridHeight === '100%' ? '100%' : undefined,
            overflow: gridHeight === '100%' ? 'hidden' : undefined,
            ...(gridHeight === '100%' ? { flex: 1 } : {}),
            ...(gridHeight == null
              ? {
                  '@media (max-height: 620px)': {
                    height: '80vh',
                  },
                }
              : gridHeight === '100%'
                ? {
                    '@media (max-height: 620px)': {
                      height: '100%',
                    },
                  }
                : {}),
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
            <NavigatorDataGrid
              rows={users ?? []}
              columns={columns}
              getRowId={(row) => row.auth_id}
              initialPageSize={initialPageSize}
              pageSizeOptions={pageSizeOptions}
              showToolbar={true}
              height="100%"
              slots={{
                toolbar: PersistentQuickFilterToolbar,
              }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                  quickFilterProps: {
                    debounceMs: 500,
                    slotProps: {
                      root: { placeholder: effectiveQuickFilterPlaceholder },
                    },
                  },
                },
              }}
              sx={{
                ...(toolbarAlignRight
                  ? {
                      '& .MuiDataGrid-toolbar': {
                        justifyContent: 'flex-end',
                      },
                    }
                  : {}),
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: COLORS.SAFEGUARD_BLUE,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: COLORS.WHITE,
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 'normal',
                  fontSize: TABLE_FONT_SIZE,
                },
                '& .MuiDataGrid-cell': {
                  fontSize: TABLE_FONT_SIZE,
                },
              }}
            />
          </Box>
          <UserDashboard
            organization={organization}
            groups={allGroups}
            allGroupUserStatuses={groupStatuses}
            pvdConfigurations={pvdConfigurations ?? undefined}
          />
        </Box>
      ) : (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Stack spacing={2} pb={5}>
            {/* Search Field for filtering */}
            <TextField
              placeholder={quickFilterPlaceholder}
              variant="outlined"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: SEARCH_FIELD_BORDER_RADIUS,
                },
              }}
            />
            {users
              .filter((user) => {
                const fullName =
                  `${user.first_name} ${user.last_name}`.toLowerCase();
                const phone = user.phone_number?.toLowerCase() || '';
                const role = user.role?.toLowerCase() || '';

                const query = debouncedSearchQuery.toLowerCase();
                return (
                  fullName.includes(query) ||
                  phone.includes(query) ||
                  role.includes(query)
                );
              })
              .map((user) => (
                <Card
                  key={user.auth_id}
                  sx={{
                    backgroundColor:
                      userToHighlight === user.auth_id
                        ? COLORS.HIGHLIGHT_YELLOW
                        : COLORS.WHITE,
                    boxShadow: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: COLORS.SAFEGUARD_BLUE,
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h6" sx={{ color: COLORS.WHITE }}>
                      {user.first_name} {user.last_name}
                    </Typography>
                    <IconButton
                      onClick={(event) => handleMenuOpen(event, user)}
                      sx={{ color: COLORS.WHITE }}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t(StringKeys.PHONE_NUMBER)}
                        </Typography>
                        <Typography sx={{ fontSize: TABLE_FONT_SIZE }}>
                          {user.phone_number}
                        </Typography>
                      </Grid>

                      <Grid size={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(StringKeys.ROLE)}
                        </Typography>
                        <RoleSelector
                          orgAzureId={organization.id}
                          orgCode={organization.org_code}
                          roleList={Object.keys(
                            organization.roles_with_features_default || {},
                          )}
                          user={user}
                          userToHighlight={setUserToHighlight}
                          sx={{ minWidth: COMPACT_SELECTOR_MIN_WIDTH }}
                          fontSize={TABLE_FONT_SIZE}
                        />
                      </Grid>

                      <Grid size={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(StringKeys.COMPASS_CONFIGURATION)}
                        </Typography>
                        <PvdConfigPicker
                          orgCode={organization.org_code}
                          configList={(pvdConfigurations ?? []).map((c) => ({ id: c.id, name: c.spec_name, is_base_config: c.is_base_config }))}
                          user={user}
                          userToHighlight={setUserToHighlight}
                          groups={orgGroups}
                          isOrgOnTrial={isOrgOnTrial}
                          fontSize={TABLE_FONT_SIZE}
                        />
                      </Grid>

                      <Grid size={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(StringKeys.EMERGENCY_CONFIGURATION)}
                        </Typography>
                        <EmergencyConfigPicker
                          orgCode={organization.org_code}
                          configList={(emergencyConfigurations ?? []).map((c) => ({ id: c.id, name: c.spec_name, is_base_config: c.is_base_config }))}
                          user={user}
                          userToHighlight={setUserToHighlight}
                          groups={orgGroups}
                          isOrgOnTrial={isOrgOnTrial}
                          fontSize={TABLE_FONT_SIZE}
                        />
                      </Grid>
                      <Grid size={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(StringKeys.GROUP)}
                        </Typography>
                        <GroupSelector
                          allGroups={allGroups}
                          currentGroup={resolveGroupName(user)}
                          user={user}
                          orgAzureId={organization.id}
                          orgCode={organization.org_code}
                          userToHighlight={setUserToHighlight}
                          fontSize={TABLE_FONT_SIZE}
                        />
                      </Grid>

                      <Grid size={12}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t(StringKeys.LAST_ACCESSED)}
                        </Typography>
                        <LastAccessedDisplay
                          timestamp={user.last_interaction_timestamp}
                        />
                      </Grid>

                      <Grid size={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: COLORS.HOVER_GRAY,
                            borderRadius: 1,
                            p: 1.5,
                            mt: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {t(StringKeys.SUBSCRIPTION)}
                          </Typography>
                          {viewerIsAdmin ? (
                            <TierSelector
                              value={getUserAssignedTier(orgGroups, user.auth_id)}
                              fullPools={fullPools}
                              disabled={!viewerIsAdmin || inProgress}
                              showTrialElevationInfo={isOrgOnTrial}
                              onChange={(tier) =>
                                changeUserTier(tier, user, resolveGroupName(user))
                              }
                              sx={{ minWidth: COMPACT_SELECTOR_MIN_WIDTH }}
                              fontSize={TABLE_FONT_SIZE}
                            />
                          ) : (
                            <Typography sx={{ fontSize: TABLE_FONT_SIZE }}>
                              {getTierLabelKey(
                                getUserAssignedTier(orgGroups, user.auth_id),
                              )}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Card>
              ))}
          </Stack>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {selectedUser && selectedUser.auth_id !== orgUser?.auth_id && (
              <MenuItem onClick={handleDeleteUserFromMenu}>
                <ListItemIcon>
                  <Delete />
                </ListItemIcon>
                <ListItemText primary={t(StringKeys.REMOVE_USER)} />
              </MenuItem>
            )}
          </Menu>
        </Box>
      )}
    </>
  );
};

export default UserTable;
